/**
 * Kaufman Adaptive Moving Average (KAMA)
 *
 * Adapts smoothing speed based on market efficiency ratio.
 * Fast market → fast MA; choppy market → slow MA.
 * Default: period=10, fastPeriod=2, slowPeriod=30.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const kamaIndicator: IndicatorDefinition = {
  id: "kama",
  name: "Kaufman Adaptive Moving Average",
  shortName: "KAMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 10, min: 2, max: 200 },
    { key: "fast", label: "Fast Period", type: "number", default: 2, min: 2, max: 50 },
    { key: "slow", label: "Slow Period", type: "number", default: 30, min: 5, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#E91E63" },
  ],
  plots: [
    { key: "kama", label: "KAMA", color: "#E91E63", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, fast = 2, slow = 30, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    if (src.length < period) return [{ key: "kama", data }];

    const fastSC = 2 / (fast + 1);
    const slowSC = 2 / (slow + 1);

    let kama = src[period - 1];
    data.push({ time: candles[period - 1].time, value: kama });

    for (let i = period; i < src.length; i++) {
      const direction = Math.abs(src[i] - src[i - period]);
      let volatility = 0;
      for (let j = i - period + 1; j <= i; j++) {
        volatility += Math.abs(src[j] - src[j - 1]);
      }
      const er = volatility === 0 ? 0 : direction / volatility;
      const sc = (er * (fastSC - slowSC) + slowSC) ** 2;
      kama = kama + sc * (src[i] - kama);
      data.push({ time: candles[i].time, value: kama });
    }

    return [{ key: "kama", data }];
  },
};
